import { Base64 } from 'js-base64'

import type {
  FileRouteContext,
  FileRouteParams,
} from '@jotta/types/schemas/FileRouteSchema'
import { EncodedRoutePath } from '@jotta/utils/path/EncodedRoutePath'
import { createPath, shiftPath } from '@jotta/utils/pathUtils'

function moduleRegex(module: FileRouteContext) {
  return new RegExp(`^\\/${module}\\/?$`, 'i')
}

const isRootApiPath = /^\/\w+\/?$/i
const isBackupApiPath = moduleRegex('backup')

const reRoutedBackupModules: FileRouteContext[] = ['photos', 'contacts']
const reRoutedBackupModuleRegexes = reRoutedBackupModules.map(module => {
  return {
    regexp: moduleRegex(module),
    module,
  }
})

function rebaseContext(
  context: FileRouteContext,
  apiPath: string,
): FileRouteContext {
  // the root of the rebased modules is the backup module
  if (
    reRoutedBackupModules.includes(context) &&
    (!apiPath || apiPath === '/' || isBackupApiPath.test(apiPath))
  ) {
    return 'backup'
  }

  // when browsing into backup, we sometimes rebase into different modules
  if (context === 'backup') {
    const rebaseMatch = reRoutedBackupModuleRegexes.find(rebase =>
      rebase.regexp.test(apiPath),
    )
    if (rebaseMatch) {
      return rebaseMatch.module
    }
  }

  return context
}

export function apiPathToRoute(
  apiPath: string,
  {
    context: rootContext = 'sync',
    sortBy = 'name',
    viewMode = 'list',
    query = '',
  }: Partial<FileRouteParams> = {},
) {
  const context = rebaseContext(rootContext, apiPath)
  let path = createPath(apiPath)
  const contextPath = createPath(context)
  const isContextApiPath =
    path.toLocaleLowerCase() === contextPath.toLocaleLowerCase()
  switch (context) {
    /** Search results use Base64 encoded api path */
    case 'search': {
      if (!query) {
        return new EncodedRoutePath('/web', context, viewMode, sortBy)
      }
      return new EncodedRoutePath(
        '/web',
        context,
        viewMode,
        sortBy,
        query,
        Base64.encodeURI(apiPath),
      )
    }
    case 'trash':
    case 'shared':
      {
        path =
          isContextApiPath || isRootApiPath.test(apiPath)
            ? shiftPath(apiPath)
            : apiPath
      }
      break

    default:
      path = shiftPath(apiPath)
      break
  }
  return new EncodedRoutePath('/web', context, viewMode, sortBy, path)
}
